/**
 * Composable Type System
 *
 * Recursive type model for test harness parameter/output types.
 * Replaces the flat GENERIC_TYPES enum with a structured, composable system.
 *
 * Serialization format is backward-compatible with existing DB strings:
 *   integer[][]  →  { kind: 'list', element: { kind: 'list', element: { kind: 'base', name: 'integer' } } }
 *   map<string, integer[]>  →  { kind: 'map', key: ..., value: ... }
 *   TreeNode  →  { kind: 'template', templateSlug: 'tree-node' }
 */

// =============================================================================
// Type Model
// =============================================================================

export type BaseTypeName = 'integer' | 'string' | 'boolean' | 'float'
export type SpecialTypeName = 'function' | 'void'

export type TypeExpression =
  | { kind: 'base'; name: BaseTypeName }
  | { kind: 'template'; templateSlug: string }
  | { kind: 'special'; name: SpecialTypeName }
  | { kind: 'list'; element: TypeExpression }
  | { kind: 'map'; key: TypeExpression; value: TypeExpression }
  | { kind: 'tuple'; elements: TypeExpression[] }

const BASE_TYPES: ReadonlySet<string> = new Set([
  'integer',
  'string',
  'boolean',
  'float',
])

const SPECIAL_TYPES: ReadonlySet<string> = new Set(['function', 'void'])

/**
 * Known template display names mapped to their slugs.
 * These are the historically hardcoded types that are actually DB templates.
 */
const KNOWN_TEMPLATE_NAMES: Record<string, string> = {
  TreeNode: 'tree-node',
  ListNode: 'list-node',
  DoublyListNode: 'doubly-list-node',
  MessageNode: 'message-node',
}

// =============================================================================
// Parser
// =============================================================================

/**
 * Parse a type string into a TypeExpression.
 *
 * Supports:
 *   - Base types: integer, string, boolean, float
 *   - Array suffix: integer[], string[][], integer[][][]
 *   - Map: map<K, V>
 *   - Tuple: tuple<T1, T2, ...>
 *   - Template names: TreeNode, ListNode, DoublyListNode, MessageNode, or any PascalCase name
 *   - Special: function
 *
 * @throws Error if the string cannot be parsed
 */
export function parseType(str: string): TypeExpression {
  const trimmed = str.trim()
  if (!trimmed) {
    throw new Error('Empty type string')
  }
  const { expr, rest } = parseTypeInner(trimmed)
  if (rest.length > 0) {
    throw new Error(`Unexpected trailing characters: "${rest}"`)
  }
  return expr
}

interface ParseResult {
  expr: TypeExpression
  rest: string
}

function parseTypeInner(str: string): ParseResult {
  // Try map<K, V>
  if (str.startsWith('map<')) {
    return parseMapType(str)
  }

  // Try tuple<T1, T2, ...>
  if (str.startsWith('tuple<')) {
    return parseTupleType(str)
  }

  // Parse atom (base type, template name, special type)
  const { expr: atom, rest: afterAtom } = parseAtom(str)

  // Consume trailing [] array suffixes
  return consumeArraySuffixes(atom, afterAtom)
}

function parseAtom(str: string): ParseResult {
  // Match a word (letters, digits, hyphens for slugs)
  const match = str.match(/^([a-zA-Z][a-zA-Z0-9-]*)/)
  if (!match) {
    throw new Error(`Expected type name at: "${str}"`)
  }

  const name = match[1]
  const rest = str.slice(name.length)

  if (BASE_TYPES.has(name)) {
    return { expr: { kind: 'base', name: name as BaseTypeName }, rest }
  }

  if (SPECIAL_TYPES.has(name)) {
    return { expr: { kind: 'special', name: name as SpecialTypeName }, rest }
  }

  // Known template display names (TreeNode → tree-node)
  if (KNOWN_TEMPLATE_NAMES[name]) {
    return {
      expr: { kind: 'template', templateSlug: KNOWN_TEMPLATE_NAMES[name] },
      rest,
    }
  }

  // Any other PascalCase or identifier — treat as template slug reference
  return { expr: { kind: 'template', templateSlug: name }, rest }
}

function parseMapType(str: string): ParseResult {
  // str starts with "map<"
  let pos = 4 // skip "map<"
  const remaining = str.slice(pos)

  // Parse key type
  const { expr: key, rest: afterKey } = parseTypeInner(remaining)
  pos = str.length - afterKey.length

  // Expect ", "
  const afterKeyTrimmed = afterKey.trimStart()
  if (!afterKeyTrimmed.startsWith(',')) {
    throw new Error(`Expected ',' in map type at: "${afterKey}"`)
  }
  const afterComma = afterKeyTrimmed.slice(1).trimStart()
  pos = str.length - afterComma.length

  // Parse value type
  const { expr: value, rest: afterValue } = parseTypeInner(afterComma)
  pos = str.length - afterValue.length

  // Expect ">"
  const afterValueTrimmed = afterValue.trimStart()
  if (!afterValueTrimmed.startsWith('>')) {
    throw new Error(`Expected '>' to close map type at: "${afterValue}"`)
  }
  const rest = afterValueTrimmed.slice(1)

  // Consume trailing [] array suffixes on the whole map type
  return consumeArraySuffixes({ kind: 'map', key, value }, rest)
}

function parseTupleType(str: string): ParseResult {
  // str starts with "tuple<"
  const remaining = str.slice(6) // skip "tuple<"
  const elements: TypeExpression[] = []

  let current = remaining
  while (true) {
    const trimmed = current.trimStart()
    if (trimmed.startsWith('>')) {
      current = trimmed.slice(1)
      break
    }

    if (elements.length > 0) {
      if (!trimmed.startsWith(',')) {
        throw new Error(`Expected ',' in tuple type at: "${current}"`)
      }
      current = trimmed.slice(1).trimStart()
    }

    const { expr, rest } = parseTypeInner(current)
    elements.push(expr)
    current = rest
  }

  if (elements.length === 0) {
    throw new Error('Tuple must have at least one element type')
  }

  // Consume trailing [] array suffixes
  return consumeArraySuffixes({ kind: 'tuple', elements }, current)
}

function consumeArraySuffixes(expr: TypeExpression, rest: string): ParseResult {
  let current = expr
  let remaining = rest
  while (remaining.startsWith('[]')) {
    current = { kind: 'list', element: current }
    remaining = remaining.slice(2)
  }
  return { expr: current, rest: remaining }
}

// =============================================================================
// Serializer
// =============================================================================

/**
 * Serialize a TypeExpression back to its canonical string form.
 */
export function serializeType(expr: TypeExpression): string {
  switch (expr.kind) {
    case 'base':
      return expr.name
    case 'special':
      return expr.name
    case 'template':
      return templateSlugToDisplayName(expr.templateSlug)
    case 'list':
      return `${serializeType(expr.element)}[]`
    case 'map':
      return `map<${serializeType(expr.key)}, ${serializeType(expr.value)}>`
    case 'tuple':
      return `tuple<${expr.elements.map(serializeType).join(', ')}>`
  }
}

/** Reverse lookup: slug → display name (e.g. 'tree-node' → 'TreeNode') */
const SLUG_TO_DISPLAY_NAME: Record<string, string> = Object.fromEntries(
  Object.entries(KNOWN_TEMPLATE_NAMES).map(([display, slug]) => [
    slug,
    display,
  ]),
)

function templateSlugToDisplayName(slug: string): string {
  return SLUG_TO_DISPLAY_NAME[slug] || slug
}

// =============================================================================
// Validation
// =============================================================================

/**
 * Check if a type string is structurally valid.
 * If knownTemplates is provided, also validates that template references exist.
 */
export function isValidType(str: string, knownTemplates?: string[]): boolean {
  try {
    const expr = parseType(str)
    if (knownTemplates) {
      const templateSet = new Set(knownTemplates)
      const referenced = getReferencedTemplates(expr)
      return referenced.every((slug) => templateSet.has(slug))
    }
    return true
  } catch {
    return false
  }
}

// =============================================================================
// Utilities
// =============================================================================

/**
 * Extract all template slugs referenced in a type expression.
 */
export function getReferencedTemplates(expr: TypeExpression): string[] {
  const slugs: string[] = []
  collectTemplates(expr, slugs)
  return [...new Set(slugs)]
}

function collectTemplates(expr: TypeExpression, slugs: string[]): void {
  switch (expr.kind) {
    case 'template':
      slugs.push(expr.templateSlug)
      break
    case 'list':
      collectTemplates(expr.element, slugs)
      break
    case 'map':
      collectTemplates(expr.key, slugs)
      collectTemplates(expr.value, slugs)
      break
    case 'tuple':
      for (const el of expr.elements) {
        collectTemplates(el, slugs)
      }
      break
    // base, special — no templates
  }
}

/**
 * Check if a type expression represents a 2D array (exactly two levels of list nesting).
 * Used for auto-format detection (matrix display).
 */
export function is2DArray(expr: TypeExpression): boolean {
  return (
    expr.kind === 'list' &&
    expr.element.kind === 'list' &&
    expr.element.element.kind !== 'list'
  )
}

/**
 * Get the template wrapper type for a given type expression.
 * Returns the template slug if the base type is a template, or undefined.
 * Handles both direct template types and list-of-template types.
 *
 * Used by the generator to determine which wrapper markers to inject.
 */
export function getTemplateSlugForWrapping(
  expr: TypeExpression,
): string | undefined {
  if (expr.kind === 'template') {
    return expr.templateSlug
  }
  // For list types, check the innermost element
  if (expr.kind === 'list') {
    return getTemplateSlugForWrapping(expr.element)
  }
  return undefined
}
